import 'date-fns';
import ptBRLocale from 'date-fns/locale/pt-BR';
import React, { useState, useEffect, useCallback } from 'react';

import DateFnsUtils from '@date-io/date-fns';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import { makeStyles } from '@material-ui/core/styles';
import { useToasts } from 'react-toast-notifications';

import { CircularProgress } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Star from '@material-ui/icons/Star';
import StarBorder from '@material-ui/icons/StarBorder';

import styles from 'assets/jss/material-dashboard-react/views/dashboardStyle.js';
import GridItem from 'components/Grid/GridItem.js';
import GridContainer from 'components/Grid/GridContainer.js';
import Table from 'components/Table/Table.js';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import Card from 'components/Card/Card';
import Button from 'components/CustomButtons/Button.js';

import api from '../../services/api';

const useStyles = makeStyles(styles);

const getFirstValue = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const formatDate = dateValue => {
  if (!dateValue) {
    return '-';
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  return parsedDate.toLocaleDateString('pt-br', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
};

const formatDateOnly = dateValue => {
  const parsedDate = new Date(dateValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const startOfDayIso = dateValue => {
  const parsedDate = new Date(dateValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  parsedDate.setHours(0, 0, 0, 0);
  return parsedDate.toISOString();
};

const endOfDayIso = dateValue => {
  const parsedDate = new Date(dateValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  parsedDate.setHours(23, 59, 59, 999);
  return parsedDate.toISOString();
};

const parseIntegerInRange = (value, min, max) => {
  const numeric = Number(value);

  if (Number.isNaN(numeric)) {
    return null;
  }

  if (numeric < min || numeric > max) {
    return null;
  }

  return Math.round(numeric);
};

const renderStars = value => {
  const score = parseIntegerInRange(value, 1, 5);

  if (!score) {
    return <span>-</span>;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {Array.from({ length: 5 }).map((_, index) =>
        index < score ? (
          <Star
            key={`star-${index}`}
            style={{ fontSize: 16, color: '#f9a825' }}
          />
        ) : (
          <StarBorder
            key={`star-${index}`}
            style={{ fontSize: 16, color: '#c7c7c7' }}
          />
        ),
      )}
      <span style={{ marginLeft: 4, fontWeight: 600 }}>{score}/5</span>
    </div>
  );
};

const renderNps = value => {
  const score = parseIntegerInRange(value, 0, 10);

  if (score === null) {
    return <span>-</span>;
  }

  let backgroundColor = '#ffebee';
  let textColor = '#b71c1c';

  if (score >= 9) {
    backgroundColor = '#e8f5e9';
    textColor = '#1b5e20';
  } else if (score >= 7) {
    backgroundColor = '#fff8e1';
    textColor = '#e65100';
  }

  return (
    <span
      style={{
        backgroundColor,
        color: textColor,
        borderRadius: 999,
        padding: '4px 10px',
        fontWeight: 700,
        fontSize: 12,
      }}
    >
      {score}/10
    </span>
  );
};

const normalizeRating = rating => {
  const customer = getFirstValue(rating.customer, rating.user, {}) || {};
  const order = getFirstValue(rating.order, rating.pedido, {}) || {};

  return {
    id: getFirstValue(rating.id, rating._id),
    order_id: (
      <Typography variant="body2" style={{ fontWeight: 600 }}>
        #{getFirstValue(order.id, order.order_id, rating.order_id, '-')}
      </Typography>
    ),
    cliente: getFirstValue(customer.name, rating.customer_name, '-'),
    delivery_score: renderStars(
      getFirstValue(rating.delivery_score, rating.deliveryScore, null),
    ),
    nps_score: renderNps(
      getFirstValue(rating.nps_score, rating.npsScore, null),
    ),
    created_at: (
      <Typography variant="body2" style={{ color: '#5f6368' }}>
        {formatDate(
          getFirstValue(
            rating.created_at,
            rating.createdAt,
            rating.data_criacao,
          ),
        )}
      </Typography>
    ),
  };
};

const getRatingCustomerName = rating => {
  const customer = getFirstValue(rating.customer, rating.user, {}) || {};
  return String(getFirstValue(customer.name, rating.customer_name, '')).trim();
};

const getRatingCreatedAt = rating => {
  const rawDate = getFirstValue(
    rating.created_at,
    rating.createdAt,
    rating.data_criacao,
    null,
  );

  if (!rawDate) {
    return null;
  }

  const parsedDate = new Date(rawDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

const extractRatingsPayload = responseData => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  if (Array.isArray(responseData?.results)) {
    return responseData.results;
  }

  if (Array.isArray(responseData?.rows)) {
    return responseData.rows;
  }

  return [];
};

export default function RatingsPage() {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const { addToast } = useToasts();
  const [loading, setLoading] = useState(false);

  const defaultStartDate = new Date(new Date() - 60 * 86400000);
  const defaultEndDate = new Date();

  const [customerName, setCustomerName] = useState('');
  const [orderId, setOrderId] = useState('');
  const [deliveryScore, setDeliveryScore] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [dataInicial, setDataInicial] = useState(defaultStartDate);
  const [dataFinal, setDataFinal] = useState(defaultEndDate);
  const [appliedFilters, setAppliedFilters] = useState({
    customerName: '',
    orderId: '',
    deliveryScore: '',
    dataInicial: defaultStartDate,
    dataFinal: defaultEndDate,
  });

  const handleDataInicialChange = date => {
    setDataInicial(date);
  };

  const handleDataFinalChange = date => {
    setDataFinal(date);
  };

  const getData = useCallback(async () => {
    try {
      setLoading(true);
      const payload = {
        page,
        limit,
      };

      if (appliedFilters.customerName) {
        const customerName = appliedFilters.customerName.trim();
        payload.customer_name = appliedFilters.customerName;
        payload.customerName = customerName;
        payload.name = customerName;
        payload.customer = customerName;
        payload.customer_nome = customerName;
      }

      if (appliedFilters.orderId) {
        payload.order_id = Number(appliedFilters.orderId);
      }

      if (appliedFilters.deliveryScore) {
        payload.delivery_score = Number(appliedFilters.deliveryScore);
      }

      if (appliedFilters.dataInicial) {
        const initialDateOnly = formatDateOnly(appliedFilters.dataInicial);
        const initialIso = startOfDayIso(appliedFilters.dataInicial);

        payload.data_inicial = initialDateOnly;
        payload.start_date = initialDateOnly;
        payload.from = initialDateOnly;
        payload.created_at_from = initialIso;
      }

      if (appliedFilters.dataFinal) {
        const finalDateOnly = formatDateOnly(appliedFilters.dataFinal);
        const finalIso = endOfDayIso(appliedFilters.dataFinal);

        payload.data_final = finalDateOnly;
        payload.end_date = finalDateOnly;
        payload.to = finalDateOnly;
        payload.created_at_to = finalIso;
      }

      const response = await api.post('/ratings/order-ratings/search', payload);

      const ratings = extractRatingsPayload(response.data);

      const filteredRatings = ratings.filter(rating => {
        const searchName = appliedFilters.customerName.trim().toLowerCase();
        const ratingName = getRatingCustomerName(rating).toLowerCase();

        const matchesName = !searchName || ratingName.includes(searchName);

        const ratingDate = getRatingCreatedAt(rating);
        const hasInitialDate = Boolean(appliedFilters.dataInicial);
        const hasFinalDate = Boolean(appliedFilters.dataFinal);

        let matchesDate = true;

        if ((hasInitialDate || hasFinalDate) && !ratingDate) {
          return false;
        }

        if (ratingDate && (hasInitialDate || hasFinalDate)) {
          const startDate = hasInitialDate
            ? new Date(appliedFilters.dataInicial)
            : null;
          const endDate = hasFinalDate
            ? new Date(appliedFilters.dataFinal)
            : null;

          if (startDate && !Number.isNaN(startDate.getTime())) {
            startDate.setHours(0, 0, 0, 0);
            matchesDate = matchesDate && ratingDate >= startDate;
          }

          if (endDate && !Number.isNaN(endDate.getTime())) {
            endDate.setHours(23, 59, 59, 999);
            matchesDate = matchesDate && ratingDate <= endDate;
          }
        }

        return matchesName && matchesDate;
      });

      setData(filteredRatings.map(normalizeRating));
      setHasNextPage(ratings.length >= limit);
    } catch (e) {
      addToast('Ocorreu um erro para atualizar os dados', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, appliedFilters, limit, page]);

  const onFilter = useCallback(() => {
    setAppliedFilters({
      customerName,
      orderId,
      deliveryScore,
      dataInicial,
      dataFinal,
    });

    if (page === 1) {
      return;
    }

    setPage(1);
  }, [customerName, dataFinal, dataInicial, deliveryScore, orderId, page]);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <h4 className={classes.cardTitleWhite}>Avaliações</h4>
            <p className={classes.cardCategoryWhite}>
              Avaliações realizadas pelos clientes.
            </p>
          </CardHeader>
          <CardBody>
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              id="order_id"
              label="Pesquise pelo número do pedido"
              name="order_id"
              value={orderId}
              onChange={e => setOrderId(e.target.value)}
            />

            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              id="customer_name"
              label="Pesquise pelo nome do cliente"
              name="customer_name"
              value={customerName}
              onChange={e => setCustomerName(e.target.value)}
            />

            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              id="delivery_score"
              label="Nota da entrega (1 a 5 estrelas)"
              name="delivery_score"
              value={deliveryScore}
              onChange={e => setDeliveryScore(e.target.value)}
            />

            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBRLocale}>
              <DatePicker
                style={{ marginTop: 15 }}
                label="Periodo inicial"
                value={dataInicial}
                onChange={handleDataInicialChange}
                format="dd/MM/yyyy"
                inputVariant="outlined"
                fullWidth
              />
            </MuiPickersUtilsProvider>

            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBRLocale}>
              <DatePicker
                style={{ marginTop: 25 }}
                label="Periodo final"
                value={dataFinal}
                onChange={handleDataFinalChange}
                format="dd/MM/yyyy"
                inputVariant="outlined"
                fullWidth
              />
            </MuiPickersUtilsProvider>

            <FormControl
              fullWidth
              variant="outlined"
              className={classes.formControl}
              style={{ marginTop: 25 }}
            >
              <InputLabel id="limit_label">Itens por página:</InputLabel>
              <Select
                labelId="limit"
                label="itens por pagina"
                variant="outlined"
                name="limit"
                fullWidth
                value={limit}
                onChange={e => setLimit(Number(e.target.value))}
              >
                <MenuItem value={10}>
                  <em>10</em>
                </MenuItem>
                <MenuItem value={20}>
                  <em>20</em>
                </MenuItem>
                <MenuItem value={50}>
                  <em>50</em>
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              color="primary"
              type="button"
              style={{ marginTop: 20 }}
              onClick={onFilter}
            >
              Filtrar
            </Button>

            {loading ? (
              <Box mt={6}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {data && data.length > 0 ? (
                  <Table
                    tableHeaderColor="primary"
                    tableHead={[
                      'Pedido',
                      'Nome',
                      'Entrega (1-5)',
                      'Recomendaria a loja (NPS 0-10)',
                      'Cadastrado em',
                      '',
                    ]}
                    tableColumn={[
                      'order_id',
                      'cliente',
                      'delivery_score',
                      'nps_score',
                      'created_at',
                      'edit',
                    ]}
                    tableColumnSize={['10%', '30%', '10%', '10%', '15%', '1%']}
                    tableData={data}
                    resource="ratings"
                  />
                ) : (
                  <p style={{ marginTop: 20 }}>
                    Nenhum avaliação cadastrada no momento.
                  </p>
                )}

                <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
                  <Button
                    color="info"
                    type="button"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage(current => Math.max(current - 1, 1))}
                  >
                    Página anterior
                  </Button>
                  <Button
                    color="info"
                    type="button"
                    disabled={!hasNextPage || loading}
                    onClick={() => setPage(current => current + 1)}
                  >
                    Próxima página
                  </Button>
                  <p style={{ margin: 0, alignSelf: 'center' }}>
                    Página atual: {page}
                  </p>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
